// Numeric helpers used by the rollup strip + list rows.
// Backend stores everything in kg; UI generally speaks MT (×1000).
// The prototype carried a 50-kg mismatch threshold; we keep the same
// constant since the backend uses it for blocker logic too.

import type { OT, OTDraftBL } from "@/features/transit/api/types"

// An OT is "locked" once it has been archived — no further edits to
// fields, lots, containers, documents, etc. Reaching TERMINER without
// archive is still editable (the user can keep amending the file
// until they explicitly close it). Archived OTs surface a read-only
// view across every panel.
//
// The `archived` flag is the source of truth rather than `stage`
// because the BE supports `unarchive`, which reopens the OT for edits
// without rewinding the stage.
export function isOTLocked(ot: OT | undefined | null): boolean {
  return !!ot?.archived
}

export const MISMATCH_THRESHOLD_KG = 50

// Sum of attached lots in kg. The list/detail responses embed the lot
// row on the link (see `OTLotLink.lot.netKg`).
export function lotsTotalKg(ot: OT | undefined | null): number {
  if (!ot?.lots) return 0
  return ot.lots.reduce((acc, link) => acc + (link.lot?.netKg ?? 0), 0)
}

export function lotsTotalMT(ot: OT | undefined | null): number {
  return lotsTotalKg(ot) / 1000
}

export function lotsTotalBags(ot: OT | undefined | null): number {
  if (!ot?.lots) return 0
  return ot.lots.reduce((acc, link) => acc + (link.lot?.bags ?? 0), 0)
}

// `draftBLs` comes back ordered, but ordering isn't a contract — we sort
// defensively by versionIdx and pick the highest.
export function latestDraftBL(ot: OT | undefined | null): OTDraftBL | null {
  if (!ot?.draftBLs?.length) return null
  return [...ot.draftBLs].sort((a, b) => a.versionIdx - b.versionIdx).slice(-1)[0] ?? null
}

export interface MismatchSummary {
  lotsKg: number
  stuffedKg: number | null
  blKg: number | null
  // |stuffed − sum(lots)| > threshold
  stuffedMismatch: boolean
  // |stuffed − latest draft BL| > threshold (only when both present)
  blMismatch: boolean
  hasMismatch: boolean
}

export function mismatchSummary(ot: OT | undefined | null): MismatchSummary {
  const lotsKg = lotsTotalKg(ot)
  const stuffedKg = ot?.totalStuffedWeightKg ?? null
  const latest = latestDraftBL(ot)
  const blKg = latest?.weightKg ?? null

  const stuffedMismatch =
    stuffedKg != null && Math.abs(stuffedKg - lotsKg) > MISMATCH_THRESHOLD_KG

  const blMismatch =
    blKg != null &&
    stuffedKg != null &&
    Math.abs(stuffedKg - blKg) > MISMATCH_THRESHOLD_KG

  return {
    lotsKg,
    stuffedKg,
    blKg,
    stuffedMismatch,
    blMismatch,
    hasMismatch: stuffedMismatch || blMismatch,
  }
}
